<template>
  <div class="propertyItem listingItem" :class="{isWithoutBorder, isVertical}">
    <template v-if="property">
      <div v-if="property?.gallery?.length" class="propertyItem__ImagesWrapper">
        <button @click="openGallery" class="propertyItem__MobileGalleryButton">
          <div v-if="property.gallery.length > 1">
            <img src="/resources/images/icons/gallery.svg" width="24" height="24" />
          </div>
          <Flare :srcset="property.gallery[0]" size="both" width="644" height="470" crop="fill" :alt="property.gallery[0]" />
        </button>
        <Modal mobile-only v-model="dialogs.gallery">
          <FlickitySlider v-if="property.gallery" class="propertyItem__Slider" :options="flickityOptions">
            <div v-for="image in property.gallery" :key="image.id" class="listingItem__ImageWrapper">
              <Flare :srcset="image" size="both" width="800" height="600" crop="fill" :alt="image" />
            </div>
          </FlickitySlider>
        </Modal>
      </div>
      <div v-else-if="property.image">
        <Flare :srcset="property.image" size="both" width="644" height="470" crop="fill" :alt="property.image" />
      </div>
  
      <div class="propertyItem__PropertyData">
        <div>
          <div class="propertyItem__Top">
            <div class="propertyItem__Location">
              <img src="/resources/images/icons/location.svg" width="24" height="24" />
              <template v-if="property.place?.name">{{property.place.name}}</template><template v-if="property.region?.name">, {{property.region.name}}</template>
            </div>
          </div>
          <h3>
            <template v-if="shortName">{{ property.name_short || property.name }}</template>
            <template v-else>{{ property.name }}</template>
          </h3>
          <div v-if="property.category_rating" class="propertyItem__Stars">
            <Icon name="star" v-for="star in property.category_rating" />
          </div>
          <div v-if="property?.usp_verticals" class="propertyItem__Extras">
            <div v-for="item in property.usp_verticals.slice(0, 3)">
              <Flare v-if="item.image" :srcset="item.image" size="both" width="24" height="24" crop="fill" :alt="item.image" />
              {{ item.description }}
            </div>
          </div>
          <div v-if="property.description" class="propertyItem__Description">
            <div v-html="property.description" />
          </div>
        </div>
  
        <div>
          <div v-if="(property.priceForPeriod || property.price_end_of_year) && !withoutPrice" class="propertyItem__AdditionalInfos">
            <div><p>{{ $t('from') }} {{ formatPrice(property.priceForPeriod?.price_from || property.price_end_of_year.price_from, property.priceForPeriod?.currency || property.price_end_of_year.currency, lang) }}</p></div>
          </div>
  
          <div class="propertyItem__ActionButtons" :class="{withoutBookButton}">
            <template v-if="isNotAvailable">
              <Button v-if="property.sef" :to="property.sef" type="nuxt-link" secondary medium>{{ $t('learnMore') }}</Button>
              <Button @click="changeDates(property)" secondary medium>{{ $t('changeDates') }}</Button>
            </template>
            <template v-else>
              <template v-if="withoutBookButton">
                <nuxt-link :to="property.sef" class="button__Text--Underline">{{ $t('learnMore') }}</nuxt-link>
              </template>
              <template v-else>
                <Button :to="property.sef" type="nuxt-link" secondary medium>{{ $t('learnMore') }}</Button>
                <Button v-if="accommodationDates" medium @click="goToAccommodation">{{ $t('bookNow') }}</Button>
              </template>
            </template>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
  const { link } = useSetup();
  const { booking } = useBooking();
  const { lang } = useLanguage();
  const { openPanel } = useReservationPanel();
  const { getAvailabilityDates, updateSearchDates } = useAvailabilityDates();
  
  const dialogs = reactive({
    gallery: false
  });
  const { open } = useModal(dialogs);

  const props = defineProps({
    property: {
      type: Object,
    },
    withoutBorder: {
      type: Boolean,
      default: false,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    withoutBookButton: {
      type: Boolean,
      default: false,
    },
    withoutPrice: {
      type: Boolean,
      default: false,
    },
    notAvailable: {
      type: Boolean,
      default: false,
    },
    shortName: {
      type: Boolean,
      default: false,
    },
  });

  const pageDotsVisible = computed(() => props.property.gallery?.length > 1);
  const isWithoutBorder = computed(() => props.withoutBorder);
  const isVertical = computed(() => props.vertical);
  const isNotAvailable = computed(() => props.notAvailable);
  const flickityOptions = {
    watchCSS: true,
    pageDots: pageDotsVisible.value,
  };
  const accommodationDates = ref(null);

  function openGallery() {
    if (props.property.gallery.length > 1) open('gallery');
  }

  function changeDates(property){
    booking.value.accommodation = property;
    booking.value.accommodation_id = property.id;
    if (booking.value.place_id) delete booking.value.place_id;
    openPanel(1)
  }

  async function goToAccommodation() {
    booking.value.accommodation_id = props.property.id;
    delete booking.value.place_id;
    updateSearchDates(props.property?.availability_calendar);
    await navigateTo({ path: link('AccommodationUnitList'), query: { accommodation_id: props.property.id } });
  }

  onMounted(() => {
    if (props.property?.availability_calendar?.length) {
      accommodationDates.value = getAvailabilityDates(props.property?.availability_calendar);
    }
  });
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_propertyItem.scss');
</style>